import React, {cloneElement, useCallback, useEffect, useMemo, useState} from "react";
import Button from "../../base/button/button";
import Dialog from "../../base/dialog";
import classnames from "classnames";
import {Box, DialogActions, DialogContent, DialogContentText, DialogTitle} from "@mui/material";
import {DvrRounded, SaveAsRounded} from "@mui/icons-material";


export const selectExportPurposes = {
    recordKeeping: "Record Keeping",
    reImporting: "Re-importing",
}

const purposes = [
    {
        id: selectExportPurposes.recordKeeping,
        title: "Record Keeping",
        icon: <DvrRounded/>,
    },
    {
        id: selectExportPurposes.reImporting,
        title: "Re-importing",
        icon: <SaveAsRounded/>,
    }
];

/**
 * @param {object} props
 * @param {boolean} props.open
 * @param {function} props.close
 * @param {function} props.onSelect
 * @param {string[]} [props.availablePurposes] Purpose ids from {@link selectExportPurposes}; defaults to all.
 */
const SelectExportPurposeDialog = ({open, close, onSelect, availablePurposes}) => {
    const [selectedPurpose, setSelectedPurpose] = useState(null);

    const visiblePurposes = useMemo(() => {
        if (!availablePurposes?.length) {
            return purposes;
        }
        return purposes.filter((purpose) => availablePurposes.includes(purpose.id));
    }, [availablePurposes]);

    /**
     * Resets or pre-selects purpose when the dialog opens/closes.
     */
    useEffect(() => {
        if (!open) {
            setSelectedPurpose(null);
            return;
        }
        setSelectedPurpose((prev) => {
            if (prev && visiblePurposes.some((purpose) => purpose.id === prev.id)) {
                return prev;
            }
            return visiblePurposes.length === 1 ? visiblePurposes[0] : null;
        });
    }, [open, visiblePurposes]);

    /**
     * Continues to the export process.
     */
    const continueToExport = useCallback(() => {
        if (selectedPurpose) {
            onSelect(selectedPurpose.id);
            close();
        }
    }, [selectedPurpose, onSelect, close]);

    return (
        <>
            <Dialog
                open={open}
                setOpen={close}
                maxWidth={'sm'}
                className={'select-export-purpose-dialog'}
            >
                <DialogTitle>
                    Select Export Format / Purpose
                </DialogTitle>
                <DialogContent dividers>
                    <DialogContentText gutterBottom sx={{marginBlockEnd: 4}}>
                        Please select the export format or purpose for your data. This will help us tailor the export to your needs.
                    </DialogContentText>
                    <div className='d-flex flex-wrap w-100 mb-4 justify-content-center'>
                        {
                            visiblePurposes.map(platform => (
                                <div
                                    key={platform.id}
                                    className={classnames('select-export-purpose-card', {'selected': platform.id === selectedPurpose?.id})}
                                    onClick={() => setSelectedPurpose(platform)}
                                >
                                    {cloneElement(platform.icon, {color: platform.id === selectedPurpose?.id ? 'primary' : 'action', sx: {marginBlockEnd: 1}})}
                                    <p>
                                        {platform.title}
                                    </p>
                                </div>
                            ))
                        }
                    </div>
                </DialogContent>
                <DialogActions className={'p-0'}>
                    <Box sx={{margin: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%'}}>
                        <Button
                            variant={'dialog'}
                            color={'primary'}
                            onClick={() => close()}
                            borderRadius={'0 8px 0 8px'}
                        >
                            Close
                        </Button>
                        <Button
                            variant={'dialog'}
                            color={'primary'}
                            type={'submit'}
                            form={'create-customer-form'}
                            borderRadius={'8px 0 8px 0'}
                            disabled={!selectedPurpose}
                            onClick={continueToExport}
                        >
                            Continue to Export
                        </Button>
                    </Box>
                </DialogActions>
            </Dialog>
        </>
    );
}

export default SelectExportPurposeDialog;
